import { createElement, type ComponentType, type ReactElement } from 'react'
import * as ReactI18next from 'react-i18next'
import type { i18n as I18nInstance } from 'i18next'

// @wehunt/ui i18n shim.
//
// This library is consumed by apps that globally augment i18next's
// `CustomTypeOptions` to type t() keys against THEIR own resources (viltrapport
// does exactly this). Because this lib's barrel is pulled into those apps' tsc
// programs, importing react-i18next's `useTranslation`/`Trans` directly makes our
// shared components' keys get checked against the host app's key union — which
// fails. These loosely-typed wrappers keep the shared components valid regardless
// of any host augmentation. Runtime behavior is identical — only the key/
// namespace TYPES are relaxed to plain strings. Shared components must import
// useTranslation/Trans from here, never from 'react-i18next' directly.
//
// NB: react-i18next symbols are accessed only INSIDE the functions below (at
// call/render time), never at module-eval time. Some consumer tests
// `vi.mock('react-i18next', ...)` with partial factories (e.g. no `Trans`); a
// factory mock throws on ANY access to a missing export, including at eval, so a
// top-level `ReactI18next.Trans` reference would break the barrel import in those
// tests. Deferring the access sidesteps that entirely.

export type UiTFunction = (key: string, options?: Record<string, unknown>) => string

export function useTranslation(ns?: string): {
  t: UiTFunction
  i18n: I18nInstance
  ready: boolean
} {
  const { t, i18n, ready } = ReactI18next.useTranslation(ns as never)
  return { t: t as unknown as UiTFunction, i18n: i18n as unknown as I18nInstance, ready }
}

// Loosely typed so i18nKey isn't checked against a host's augmented key union.
export function Trans(props: Record<string, unknown>): ReactElement {
  const Impl = ReactI18next.Trans as unknown as ComponentType<Record<string, unknown>>
  return createElement(Impl, props)
}
